import "./LicenseSelector.css";
import { useEffect, useRef, useState } from "react";
import { useSSEContext } from "../../contexts/sse";
import { useUserStore } from "../../contexts/userStore";
import MqttService from "../../services/MqttService";
import { useAuth } from "../../contexts/auth";
import { MdOutlineMapsHomeWork } from "react-icons/md";
import { formatDate } from "../../utils/date";
import { FaChevronRight } from "react-icons/fa6";
import { IoKey } from "react-icons/io5";
import { FiUploadCloud } from "react-icons/fi";
import { useBackup } from "../../contexts/backup";
import { BsCpu } from "react-icons/bs";
import { getOnlineOfflineDevices, isHardwareOnline } from "../../helpers/DeviceHelper";
import { isNumeric } from "../../utils/number";
import EventsService from "../../services/EventsService";
import { subscribe, unsubscribe } from "../../utils/events";
import { wait } from "../../utils/timer";
import { TbBuildingCommunity } from "react-icons/tb";
import { TimeAgo } from "../TimeAgo/TimeAgo";

function LicenseItem({ unit, setModalIsOpen, getStatus, licenses }) {
	const { selectedCondominio, changeSelectedCondominio, setSelectedCondominio, setSelectedCondominioObj } = useUserStore();
	const [isExpanded, setIsExpanded] = useState(false);

	if (!unit) return null;

	const unitLicenses = licenses.filter(licence => `${licence.id_condominium}` === unit.id);

	return (
		<li key={unit.id} className={`unit-wrapper`}>
			<span className={`unit-item ${selectedCondominio === unit.id ? "selected" : ""}`}>
				{unit.children && unit.children.length > 0 && (
					<button type="button" className={`unit-children-toggle`} onClick={() => setIsExpanded(!isExpanded)}>
						<span className="toggle-icon">
							<FaChevronRight className={`${isExpanded && 'rotate-90'}`} />
						</span>
					</button>
				)}
				<button type="button" className="unit-bar" disabled={!unit.selectable} onClick={() => {
					if (selectedCondominio !== unit.id && unit.selectable) {
						changeSelectedCondominio(parseInt(unit.id));
						// setSelectedCondominio(parseInt(unit.id));
						// setSelectedCondominioObj(unit);
						setModalIsOpen(false);
						getStatus?.(unit.id);
					}
				}}>
					<span className="unit-name">{unit.name}</span>
					<span className="unit-info">
						{unitLicenses && unitLicenses.length > 0 && (
							<span className="unit-license-info">
								<span>
									<IoKey />
									<span>
										Expira em {formatDate(`${unitLicenses[0].expirationDate}`)}{' '}
										<b className="font-bold!">
											(<TimeAgo date={new Date(`${unitLicenses[0].expirationDate}`)} simple={true} showText={false} />)
										</b>
									</span>
								</span>
								<span>
									<BsCpu />
									<span>{unitLicenses[0].serialNumber ?? 'Sem equipamento'}</span>
								</span>
							</span>
						)}
						{!unit.parentId && (
							<span className="badge-white">Principal</span>
						)}
					</span>
				</button>
			</span>
			{unit.children && unit.children.length > 0 && (
				<ul className={`unit-list ${isExpanded ? 'expanded' : ''}`}>
					{unit.children.map((child, idx) =>
						<LicenseItem key={idx} unit={child} setModalIsOpen={setModalIsOpen} getStatus={getStatus} licenses={licenses} />
					)}
				</ul>
			)}
		</li>
	);
}

export function LicenseSelector(props) {
	const { selectedCondominioObj, selectedCondominio, licences, condominiums, clientLicenses, selectingUnit } = useUserStore();
	const { user } = useAuth();
	const { sseReceived } = useSSEContext();
	const { backup, error: backupError } = useBackup();
	const [isOnline, setIsOnline] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [modalIsOpen, setModalIsOpen] = useState(false);
	const [treeUnits, setTreeUnits] = useState([]);
	const [currentHardware, setCurrentHardware] = useState(null);
	const modalRef = useRef(null);
	const buttonRef = useRef(null);

	const updateHardwareStatus = async (unitId) => {
		if (!unitId) return;
		setIsLoading(true);
		const selectedHardware = await EventsService.getHardwareByUnit(Number(unitId));
		if (selectedHardware?.data) {
			setCurrentHardware(selectedHardware.data);
			setIsOnline(isHardwareOnline(selectedHardware.data));
		}
		setIsLoading(false);
		return selectedHardware.data;
	};

	useEffect(() => {
		// function onGlobalClick(e) {
		// 	if (
		// 		modalIsOpen &&
		// 		!e.target.contains(modalRef.current) &&
		// 		!e.target.contains(buttonRef.current) &&
		// 		!modalRef.current.contains(e.target) &&
		// 		!buttonRef.current.contains(e.target)
		// 	) {
		// 		setModalIsOpen(false);
		// 	}
		// }
		function onKeyUp(e) {
			if (e.key === "Escape" && modalIsOpen) {
				setModalIsOpen(false);
			}
		}
		document.addEventListener("keyup", onKeyUp);
		// document.addEventListener("click", onGlobalClick);
		return () => {
			// document.removeEventListener("click", onGlobalClick);
			document.removeEventListener("keyup", onKeyUp);
		};
	}, [modalIsOpen]);

	useEffect(() => {
		updateHardwareStatus(selectedCondominio);
		function onSseConnect() {
			updateHardwareStatus(selectedCondominio);
		}
		function onSelectLicenseRequest(open) {
			console.log('onSelectLicenseRequest: ', open);
			setModalIsOpen(open);
		}
		subscribe('onSseConnect', onSseConnect);
		subscribe('onSelectLicenseRequest', onSelectLicenseRequest);
		return () => {
			unsubscribe('onSseConnect', onSseConnect);
			unsubscribe('onSelectLicenseRequest', onSelectLicenseRequest);
		}
	}, [selectedCondominio]);

	useEffect(() => {
		const interval = setInterval(() => {
			const on = isHardwareOnline(currentHardware);
			setIsOnline(prev => prev !== on ? on : prev);
		}, 1000 * 30);
		return () => clearInterval(interval);
	}, [currentHardware]);

	useEffect(() => {
		if (sseReceived?.action === "hardwaresLastCommunications") {
			const { hardwares } = sseReceived.content;
			const { offlineHardwares, onlineHardwares } = getOnlineOfflineDevices(hardwares);
			const all = [
				...onlineHardwares.map(device => ({ ...device, on: true })),
				...offlineHardwares.map(device => ({ ...device, on: false })),
			];
			const currentDevice = all.find((item) => item.id_condominium === Number(selectedCondominio));
			if (currentDevice) {
				setCurrentHardware(currentDevice);
				setIsOnline(currentDevice.on);
			}
		}
	}, [sseReceived]);

	function buildTreeUnits(_units) {
		if (!Array.isArray(clientLicenses) || clientLicenses.length === 0) return [];
		const unitsToShowMap = new Map();
		const unitsMap = new Map();
		for (const unit of condominiums) {
			if (clientLicenses?.find(clientLicense => clientLicense.id_condominium && Number(clientLicense.id_condominium) === Number(unit.id))) {
				unitsMap.set(unit.id, unit);
				unitsToShowMap.set(unit.id, unit);
				for (const ancestorId of unit.ancestorIds) {
					const ancestor = condominiums.find(condominium => Number(condominium.id) === Number(ancestorId));
					if (ancestor && ancestor.parentId) {
						unitsToShowMap.set(ancestor.id, ancestor);
					}
				}
			}
		}
		const unitsToShow = Array.from(unitsToShowMap.values());

		function traverseUnits(unit) {
			if (!unit) return;
			unitsToShow.forEach((u, idx) => {
				if (unit.id !== u.parentId) {
					return;
				}
				unit.children = unit.children || [];
				unit.children.push(traverseUnits({
					...u,
					selectable: unitsMap.has(u.id) || true,
				}));
			});
			return unit;
		}
		const root = unitsToShow.find(unit => unit.parentId === null);
		const tree = [];
		if (root) {
			tree.push(traverseUnits({ ...root }));
		} else {
			unitsToShow.filter(unitA => !unitsToShow.some(unitB => unitA.ancestorIds.includes(unitB.id)))?.forEach(unit => {
				tree.push(traverseUnits({
					...unit,
					selectable: unitsMap.has(unit.id) || true,
					name: `${unit.parent.parentId && !unit.isSubOrganization ? `${unit.parent.name} / ` : ''}${unit.name}`
				}));
			});
		}
		return tree;
	}

	useEffect(() => {
		const units = condominiums.filter(unit => unit.organizationId === user.userData.selectedOrganizationId).sort((a, b) => a.level - b.level) || [];
		if (units.length === 0) {
			setTreeUnits([]);
			return;
		}
		setTreeUnits(buildTreeUnits(units));
	}, [condominiums, clientLicenses]);

	function mountTreeUnits() {
		if (!treeUnits || Object.keys(treeUnits).length === 0) return null;
		return treeUnits.map((unit, idx) =>
			<LicenseItem key={idx} unit={unit} setModalIsOpen={setModalIsOpen} licenses={clientLicenses} />
		);
	}

	const hasUnitWithLicense = condominiums?.length > 0 && clientLicenses?.length > 0 && clientLicenses.some(clientLicense => condominiums.some(condominium => Number(condominium.id) === Number(clientLicense.id_condominium)));
	const hasBackupInProgress = backup && backup.status === 4 && (backup.currentStage < backup.totalImportingStages || backupError);
	const backupProgress = isNumeric(backup?.currentStage) && isNumeric(backup?.totalImportingStages) ? backup?.currentStage / backup?.totalImportingStages : 0;

	return user.userData.selectedOrganization && selectedCondominioObj ? (
		<>
			<div className="unit-selector">
				<span className="unit-selector-backoverlay"></span>
				<button ref={buttonRef} type="button" onClick={() => setModalIsOpen(!modalIsOpen)}>
					<span className="unit-bar">
						{(hasBackupInProgress) ? (
							<span className="unit-backup-status" style={{
								backgroundImage: `conic-gradient(from 0deg, #69e356 ${backupProgress * 360}deg, transparent 0deg)`
							}}>
								<span><FiUploadCloud /></span>
							</span>
						) : (
							<span className={`unit-avatar ${selectingUnit ? 'shimmer' : ''}`}>
								{!selectingUnit && (
									<b>{selectedCondominioObj.name.split(' ').map(char => char.charAt(0)).splice(0, 2).join('')}</b>
								)}
							</span>
						)}
						{!props.collapsed && (
							<span className="unit-info">
								{!selectingUnit && (
									<span className={`org-name`}>
										{hasBackupInProgress ? `Importação em andamento (${Math.round(backupProgress * 100)}%)` : (selectedCondominioObj.parentId && !selectedCondominioObj.isSubOrganization ? selectedCondominioObj.parent?.name : 'Principal')}
									</span>
								)}
								<span className="unit-name">
									{selectingUnit ? 'Carregando...' : selectedCondominioObj.name}
								</span>
							</span>
						)}
					</span>
					{!props.collapsed && selectedCondominioObj.parentId && licences && licences.length > 0 && (
						<>
							{(isLoading || selectingUnit) ? (
								<span className="status-tag shimmer"></span>
							) : (
								<span className={`status-tag ${isOnline ? "on" : "off"}`}>
									{isOnline ? "ON" : "OFF"}
								</span>
							)}
						</>
					)}
				</button>
			</div>
			{!selectingUnit && (
				<div className="modal-unit-selector" data-open={modalIsOpen}>
					<div className="modal-unit-selector-overlay" onClick={(e) => setModalIsOpen(false)}></div>
					<div ref={modalRef} className="modal-unit-selector-content">
						<div className="modal-unit-selector-header">
							<span className="modal-unit-selector-title">
								<TbBuildingCommunity size={20} />
								<h2>Selecione a licença</h2>
							</span>
							<button type="button" className="close-button" onClick={(e) => setModalIsOpen(false)}>
								<span className="close-icon">&times;</span>
							</button>
						</div>
						<div className="modal-unit-selector-body">
							{hasUnitWithLicense ? (
								<ul className="unit-list root-item">
									{mountTreeUnits()}
								</ul>
							) : (
								<div className="no-events-wrapper box-border! min-h-auto! flex-1!">
									<h2 className="text-lg! leading-[1.1]!">Nenhuma licença encontrada</h2>
								</div>
							)}
						</div>
					</div>
				</div>
			)}
		</>
	) : (
		<></>
	);
}